// UI Components
import {
  Badge,
  Divider,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Flex,
  ListItem,
  Text,
  VisuallyHidden,
} from '@/components/ui';
import React, { useMemo } from 'react';
import {
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconColumns,
  IconColumns2,
  IconDatabase,
  IconWand,
  IconX,
} from '@tabler/icons-react';

// Types
import { ColumnMapping, FormField } from './types';

interface FieldColumnSelectorProps {
  field:
    | FormField
    | {
        dataPoint: string;
        label: string;
        type: 'metadata' | 'formField';
        required: boolean;
      };
  fileColumns: string[];
  fileMappings: ColumnMapping[];
  fileId: string;
  onMappingChange: (
    fileId: string,
    columnName: string,
    mappingType: 'formField' | 'metadata' | 'ignored',
    targetField?: string
  ) => void;
  triggerLabel?: string;
  triggerIcon?: React.ReactNode;
  triggerVariant?: 'surface' | 'dashed' | 'soft' | 'solid';
  triggerColor?: 'success' | 'info' | 'default' | 'warning' | 'danger';
}

export const FieldColumnSelector: React.FC<FieldColumnSelectorProps> = ({
  field,
  fileColumns,
  fileMappings,
  fileId,
  onMappingChange,
  triggerLabel,
  triggerIcon,
  triggerVariant,
  triggerColor,
}) => {
  type SelectorColor = NonNullable<FieldColumnSelectorProps['triggerColor']>;

  const toBadgeColor = (
    color: SelectorColor
  ): React.ComponentProps<typeof Badge>['color'] => {
    switch (color) {
      case 'danger':
        return 'error';
      default:
        return color;
    }
  };
  // Get current mapping for this field
  const currentMapping = useMemo(() => {
    // Determine mapping type based on field type
    const mappingType = field.type === 'metadata' ? 'metadata' : 'formField';
    const targetValue = field.dataPoint;

    return fileMappings.find(
      m =>
        m.fileId === fileId &&
        m.mappingType === mappingType &&
        (mappingType === 'metadata'
          ? m.metadataType?.type === targetValue
          : m.targetField === targetValue)
    );
  }, [fileMappings, fileId, field.dataPoint, field.type]);

  // Get available columns (not already mapped to other fields)
  const availableColumns = useMemo(() => {
    const mappedColumns = new Set<string>();

    // Find all columns that are already mapped to the same type of fields
    const targetMappingType =
      field.type === 'metadata' ? 'metadata' : 'formField';

    fileMappings.forEach(cm => {
      // Only exclude columns that are actually mapped to the same target (not ignored)
      if (cm.mappingType === targetMappingType) {
        // For metadata, check if it's mapped to a different metadata type
        if (targetMappingType === 'metadata') {
          if (cm.metadataType?.type !== field.dataPoint) {
            mappedColumns.add(cm.columnName);
          }
        } else {
          // For form fields, check if it's mapped to a different field
          if (cm.targetField !== field.dataPoint) {
            mappedColumns.add(cm.columnName);
          }
        }
      }
      // Note: ignored mappings don't block column selection
    });

    return fileColumns.filter(columnName => !mappedColumns.has(columnName));
  }, [fileMappings, fileColumns, field.dataPoint, field.type]);

  // Note: validationErrors not available in legacy ColumnMapping type
  const validationErrors: string[] = []; // TODO: Add validation errors support

  const handleMapToColumn = (columnName: string) => {
    // Determine mapping type based on field type
    const mappingType = field.type === 'metadata' ? 'metadata' : 'formField';
    onMappingChange(fileId, columnName, mappingType, field.dataPoint);
  };

  const handleClearMapping = () => {
    if (currentMapping) {
      onMappingChange(fileId, currentMapping.columnName, 'ignored');
    }
  };

  const handleAutoMap = () => {
    // Try to find exact match by field name
    const exactMatch = fileColumns.find(
      columnName => columnName.toLowerCase() === field.dataPoint.toLowerCase()
    );

    if (exactMatch && availableColumns.includes(exactMatch)) {
      handleMapToColumn(exactMatch);
    }
  };

  const getStatusColor = ():
    | 'success'
    | 'info'
    | 'warning'
    | 'danger'
    | 'default' => {
    if (validationErrors.length > 0) return 'danger';

    // If we have a current mapping, show appropriate color
    if (currentMapping) {
      if (field.type === 'metadata') return 'info'; // Metadata mappings are blue
      return 'success'; // Form field mappings are green
    }

    // If no mapping, color depends on field type and required status
    if (field.type === 'metadata') {
      return field.required ? 'danger' : 'warning'; // Required metadata = red, optional = orange
    }

    return 'warning'; // Unmapped form fields
  };

  const getStatusVariant = () => {
    // If we have a current mapping, always use surface
    if (currentMapping) {
      return 'surface';
    }

    // If no mapping, use dashed for all unmapped fields
    return 'dashed';
  };

  const getStatusIcon = () => {
    if (validationErrors.length > 0) {
      return <IconX color="var(--red-9)" />;
    }

    // If we have a current mapping, show appropriate icon
    if (currentMapping) {
      if (field.type === 'metadata') {
        return <IconDatabase />;
      }
      return <IconCheck />;
    }

    // If no mapping, show warning/error icon
    if (field.type === 'metadata' && field.required) {
      return <IconAlertTriangle />;
    }

    return field.type === 'metadata' ? <IconDatabase /> : <IconColumns />;
  };

  const getMappingDisplayText = () => {
    if (validationErrors.length > 0) {
      return 'Validation Warning';
    }

    if (currentMapping) {
      return currentMapping.columnName;
    }

    return 'Select column';
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Badge
          variant={triggerVariant || getStatusVariant()}
          size="xsmall"
          color={toBadgeColor(triggerColor || getStatusColor())}
          startIcon={triggerIcon !== undefined ? triggerIcon : getStatusIcon()}
          endIcon={<IconChevronDown />}
          style={{
            cursor: 'pointer',
          }}
        >
          {triggerLabel !== undefined ? triggerLabel : getMappingDisplayText()}
          <VisuallyHidden>
            {currentMapping?.confidence &&
              currentMapping.confidence < 1.0 &&
              currentMapping.confidence > 0 && (
                <Badge size="xsmall" color="orange" variant="soft">
                  {Math.round(currentMapping.confidence * 100)}%
                </Badge>
              )}
          </VisuallyHidden>
        </Badge>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        variant="soft"
        style={{ minWidth: '280px', maxHeight: '350px', overflow: 'auto' }}
      >
        {/* Current mapping info */}
        {currentMapping && (
          <>
            <Badge
              startIcon={<IconCheck color="var(--green-9)" />}
              size="small"
              color="success"
              variant="surface"
              mb='1'
            >
              Currently mapped to: <strong>{currentMapping.columnName}</strong>
            </Badge>

            <DropdownMenuItem asChild>
              <ListItem
                size={'xsmall'}
                startIcon={<IconX color="var(--red-9)" />}
                primary={<Text size='2xsmall' weight='medium'>Clear Mapping</Text>}
                // secondary="Remove this field mapping"
                onClick={handleClearMapping}
              />
            </DropdownMenuItem>
            <Divider my='1' />
          </>
        )}

        {/* Available columns */}
        {availableColumns.length > 0 && (
          <>
            <DropdownMenuLabel>
              Available Columns ({availableColumns.length})
            </DropdownMenuLabel>
            {availableColumns.map(columnName => (
              <DropdownMenuItem key={columnName} asChild>
                <ListItem
                  primary={
                    <Flex align="center" gap="2">
                      <IconColumns2 color="var(--blue-9)" />
                      <Text size='2xsmall' weight='medium'>{columnName}</Text>
                      {columnName.toLowerCase() ===
                        field.dataPoint.toLowerCase() && (
                        <Badge size="xsmall" color="success" variant="surface">
                          Exact Match
                        </Badge>
                      )}
                    </Flex>
                  }
                  // secondary={
                  //   columnName.toLowerCase() === field.dataPoint.toLowerCase()
                  //     ? 'Perfect name match'
                  //     : 'Available for mapping'
                  // }
                  onClick={() => handleMapToColumn(columnName)}
                />
              </DropdownMenuItem>
            ))}
          </>
        )}

        {/* No available columns */}
        {availableColumns.length === 0 && !currentMapping && (
          <div style={{ padding: '12px', textAlign: 'center' }}>
            <Text size="1" color="gray">
              No available columns. All columns are already mapped to other
              fields.
            </Text>
          </div>
        )}

        {/* Auto-map option */}
        {!currentMapping && availableColumns.length > 0 && (
          <>
            <Divider my='1' />
            <DropdownMenuItem asChild>
              <ListItem
                startIcon={<IconWand color="var(--accent-main)" />}
                size="small"
                primary={<Text size='2xsmall' weight='medium'>Try Auto-Map</Text>}
                // secondary="Find column with matching name"
                onClick={handleAutoMap}
              />
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default FieldColumnSelector;
